// === SAA:UTILS:SHELL-NAVIGATION ===
// What the app shell does on a logo click and on a completed progress-step click.
//   Logo: back to the product selection WITHOUT a reset, so the user can add or
//         swap products. Once the aanvraag is sent the workflow is reset first, so
//         the next click starts a clean new aanvraag. While it is still BEING sent
//         the click does nothing — see resolveLogoNavigation.
//   Progress steps: the config step ids (workflow.progressSteps /
//         applicationProgressSteps) map to routes here; "product-details" goes to
//         the first selected product that still needs a premium, or the last
//         selected product when every product is priced. Back is always allowed;
//         FORWARD only up to the validated frontier (utils/progressFrontier: walked to
//         via Volgende AND nothing changed since) and while every step before the
//         target still passes its own gate (utils/stepCompleteness). An unchanged flow
//         travels freely both ways; a change (products, client field, recalculated
//         premium, payment, answers) closes the later steps until walked through again.
import type { ProgressStepClick } from '@sluitstraat/core';
import { getProductOrder } from '../workflowConfig';
import { hasCalculatedPremium } from './productPremium';
import { clearSaaSessionState } from './workflowReset';
import { isRequestInFlight, isRequestSent } from './requestState';
import { STEP_COMPLETE, type WorkflowReads } from './stepCompleteness';
import {
  PROGRESS_FLOW_STEPS,
  arriveAtProgressPosition,
  effectiveFrontier,
  progressPositionForPath,
} from './progressFrontier';

export const PRODUCT_SELECTION_ROUTE = '/workflow/product-selection';

const PROGRESS_STEP_ROUTES: Record<string, string> = {
  products: PRODUCT_SELECTION_ROUTE,
  'client-information': '/workflow/client-information',
  quote: '/workflow/summary',
  payment: '/workflow/payment-information',
  questions: '/workflow/acceptance-questions',
  confirm: '/workflow/confirmation',
};

export type ProductProgressReads = WorkflowReads;

/** Record the position the user arrived at (every route change); re-baselines the frontier. */
export const trackProgressArrival = (pathname: string, reads: WorkflowReads): void => {
  const pos = progressPositionForPath(pathname);
  if (pos) arriveAtProgressPosition(pos.flow, pos.index, reads);
};

/**
 * Back: always. Forward: only up to the validated frontier (walked to, unchanged since)
 * and while every step before the target is complete on the current data (its own
 * Volgende gate). The current step is never clickable.
 */
export const isProgressStepClickable = (step: ProgressStepClick, reads: WorkflowReads): boolean => {
  if (step.index === step.currentIndex) return false;
  if (step.index < step.currentIndex) return true;
  if (effectiveFrontier(step.flowType, reads) < step.index) return false;
  const ids = PROGRESS_FLOW_STEPS[step.flowType] ?? [];
  return ids.slice(0, step.index).every((id) => STEP_COMPLETE[id]?.(reads) ?? false);
};

/** First selected product without a premium; the last selected product when all are priced. */
export const resolveProductDetailsRoute = ({ selectedProducts, getStepData }: ProductProgressReads): string | null => {
  const selected = getProductOrder().filter((id) => selectedProducts?.[id]);
  if (selected.length === 0) return null;
  const open = selected.find((id) => !hasCalculatedPremium(id, getStepData(id)));
  return `/workflow/${open ?? selected[selected.length - 1]}`;
};

export const resolveProgressStepRoute = (step: ProgressStepClick, reads: ProductProgressReads): string | null =>
  step.id === 'product-details' ? resolveProductDetailsRoute(reads) : PROGRESS_STEP_ROUTES[step.id] ?? null;

/**
 * Logo click: product selection, keeping the workflow unless the aanvraag has been sent.
 *
 * While the createRequest POST is still running the click is IGNORED (null). The request is not
 * abortable: tearing the workflow down mid-flight does not stop it reaching the backoffice, and
 * when it resolves the confirmation screen's live closure writes its submitted marker back into
 * the sessionStorage we just cleared and navigates to the end page — which bounces straight back
 * to product selection because the workflow context is empty. The aanvraag would have succeeded
 * without the user ever learning it, leaving them free to file a duplicate.
 */
export const resolveLogoNavigation = (
  clearWorkflowData: () => void,
): { route: string; replace: boolean } | null => {
  if (isRequestInFlight()) return null;
  if (isRequestSent()) {
    clearSaaSessionState();
    clearWorkflowData();
    return { route: PRODUCT_SELECTION_ROUTE, replace: true };
  }
  return { route: PRODUCT_SELECTION_ROUTE, replace: false };
};
